// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeroLayout1Data } from '../layout_1/hero-layout-1.component';

/**
 * Preview component for the "Hero Layout 5" template that renders hero content (image, title, paragraph, and primary action) with configurable styles and emits button click events.
 * @component
 * @selector app-hero-layout-5-preview
 * @example
 *   <app-hero-layout-5-preview
 *     [data]="sampleHeroData"
 *     [headingColor]="'#000'"
 *     [paragraphColor]="'#666'"
 *     [headingFont]="'Arial, sans-serif'"
 *     [bodyFont]="'Roboto, sans-serif'"
 *     [primaryColor]="'#ff5722'"
 *     [titleStyles]="{ 'font-weight': '700' }"
 *     (buttonClick)="onHeroButtonClick($event)">
 *   </app-hero-layout-5-preview>
 * @input {HeroLayout1Data | null} data - The data model for the hero section (titles, copy, image object or imageUrl).
 * @input {string | null} headingColor - Optional color for the heading text.
 * @input {string | null} paragraphColor - Optional color for the paragraph/body text.
 * @input {string | null} headingFont - Optional font-family for the heading.
 * @input {string | null} bodyFont - Optional font-family for the body text.
 * @input {string | null} primaryColor - Optional primary color used for buttons/accents.
 * @input {any} titleStyles - Inline style overrides for the title element (object of CSS properties).
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when the hero primary button is activated; payload contains the target link and the original DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): Validates the presence of a link and emits the buttonClick event with { link, event } if provided. Intended to be called from the template when the primary action is triggered.
 * - getImageSrc(): string: Resolves the image source by checking data.image.src, data.imageUrl, or returning an empty string when none available.
 * - getImageAlt(): string: Returns a sensible alt text from the data model or a default ("Car Wash") when missing.
 * - getBackgroundImage(): string: Returns a CSS background-image value (e.g., url(...)) when an image source exists, otherwise an empty string.
 *
 * Lifecycle hooks:
 * - No Angular lifecycle hooks are implemented in this preview component; it relies on input bindings and template interactions. Any initialization or reactive behavior should be handled by the parent or via input changes.
 */
@Component({
  selector: 'app-hero-layout-5-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-5.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-5.component.scss'],
})
export class HeroLayout5PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  getBackgroundImage(): string {
    const imageSrc = this.getImageSrc();
    return imageSrc ? `url(${imageSrc})` : '';
  }
}
